import { trackV2 } from '@gumtree/shared/src/util/track-ga-event';
import axios from 'axios';
import { trackGA4Event } from '@gumtree/shared/src/util/ga4-shared';
import getSaveSearch, { SaveSearchFormState } from '../get-data/transform/save-search';

type InfoDialogContentMode = 'NOT_LOGGED_IN' | 'ERROR' | 'LIMIT_REACHED' | 'CONFIRMED';

type State = ReturnType<typeof getSaveSearch> & {
    isLoading: boolean;
    showInfoDialog: boolean;
    infoDialogContentMode: InfoDialogContentMode;
};

const initialState: State = {
    isLoading: false,
    hasSearchBeenSaved: false,
    formState: undefined,
    showInfoDialog: false,
    infoDialogContentMode: 'ERROR',
};

type AddAlertAjaxResponseOfSuccess = { errorNameValuePairs: []; id: string; success: true };
type AddAlertAjaxResponseOfOverTheLimit = {
    errorNameValuePairs: [
        {
            name: 'VALIDATION_FAILED';
            value: 'Validation error';
        }
    ];
    success: false;
};

const SET_LOADING = 'SET_LOADING';
const SET_SAVED_SEARCHES = 'SET_SAVED_SEARCHES';
const MARK_SEARCH_AS_SAVED = 'MARK_SEARCH_AS_SAVED';
const OPEN_INFO_DIALOG = 'OPEN_INFO_DIALOG';
const CLOSE_INFO_DIALOG = 'CLOSE_INFO_DIALOG';

export const setLoading = (): { type: string } => ({
    type: SET_LOADING,
});

export const setSavedSearches = (
    payload: ReturnType<typeof getSaveSearch>
): { type: string; payload: ReturnType<typeof getSaveSearch> } => ({
    type: SET_SAVED_SEARCHES,
    payload,
});

export const markSearchAsSaved = (): {
    type: string;
} => ({
    type: MARK_SEARCH_AS_SAVED,
});

export const openInfoDialog = (
    infoDialogContentMode: InfoDialogContentMode
): {
    type: string;
    payload: { infoDialogContentMode: InfoDialogContentMode };
} => ({
    type: OPEN_INFO_DIALOG,
    payload: { infoDialogContentMode },
});

export const closeInfoDialog = (): { type: string } => ({
    type: CLOSE_INFO_DIALOG,
});

export const updateSavedSearchesThunk =
    (payload: SaveSearchFormState, token: string | undefined) => (dispatch) => {
        dispatch(setLoading());

        const bodyFormData = new FormData();
        for (const key in payload) {
            if (payload.hasOwnProperty(key)) {
                bodyFormData.append(key, payload[key]);
            }
        }

        axios({
            method: 'POST',
            url: '/my-account/saved-searches/add',
            data: bodyFormData,
            headers: {
                accept: 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
                X_CSRF_TOKEN: token,
            },
            timeout: 10000,
        })
            .then(async function (response) {
                if (response) {
                    if (response.status === 200) {
                        const data:
                            | AddAlertAjaxResponseOfSuccess
                            | AddAlertAjaxResponseOfOverTheLimit = response.data;
                        if (data.success) {
                            trackGA4Event<GA4.SearchAlertAction>({
                                event: 'search_alert_saved',
                                searchAlertCategory: payload?.search_category,
                                searchAlertName: payload?.q,
                            });

                            dispatch(markSearchAsSaved());
                            trackV2('SRP-savesearch-success');
                        } else {
                            dispatch(openInfoDialog('LIMIT_REACHED'));
                            trackV2('SRP-savesearch-limit');
                        }
                    } else {
                        dispatch(openInfoDialog('ERROR'));
                        trackV2('SRP-savesearch-error');
                    }
                }
            })
            .catch((ex) => {
                if (ex?.response?.status === 401) {
                    dispatch(openInfoDialog('NOT_LOGGED_IN'));
                    trackV2('SRP-savesearch-notloggedin');
                } else {
                    dispatch(openInfoDialog('ERROR'));
                    trackV2('SRP-savesearch-error');
                }
            });
    };

type Action = ReturnType<typeof setSavedSearches> &
    ReturnType<typeof markSearchAsSaved> &
    ReturnType<typeof openInfoDialog> &
    ReturnType<typeof closeInfoDialog>;

export default (state = initialState, action: Action): State => {
    switch (action.type) {
        case SET_LOADING:
            return { ...state, isLoading: true };
        case SET_SAVED_SEARCHES:
            return { ...state, ...action.payload };
        case MARK_SEARCH_AS_SAVED:
            return {
                ...state,
                isLoading: false,
                hasSearchBeenSaved: true,
                showInfoDialog: true,
                infoDialogContentMode: 'CONFIRMED',
            };
        case OPEN_INFO_DIALOG:
            return {
                ...state,
                isLoading: false,
                showInfoDialog: true,
                infoDialogContentMode: action.payload.infoDialogContentMode,
            };
        case CLOSE_INFO_DIALOG:
            return { ...state, showInfoDialog: false };
        default:
            return state;
    }
};
